import { findProblemsBeforeRender } from "../scripts/findErrorLayers";
import { rootStore } from "../../store/root";
import { AsyncOpState, SaveCompStep } from "../../store/utils/async-op-state";
import { defaultSettings } from "../defaultSettings";
import { runJsxScript } from "../helpers/runJsxScript";
import { createTmpDir } from "../helpers/tmpDir";
import { jsxConstFunctions } from "../jsxConsts";
import { runBeforeRender } from "./runBeforeRender";
import { analyticsInstance } from "../../analytics/analyticsCtrls";
import { fileFormat } from "../../store/utils/utilMethods";

export const maxFilesSizeLimit = 5;

function getNextComposition() {
  const {
    compositionStore: {
      allRenderedCompositions,
      sortedQueuedCompositions,
      allCompositions,
      setCompositionCurrentlyRendering,
    },
  } = rootStore;
  // Reset
  setCompositionCurrentlyRendering({ id: 0, name: "" });
  // Loops through the selected compositions and returns a composition that has not been rendered yet (or null otherwise)
  for (const comp of sortedQueuedCompositions) {
    if (
      !allRenderedCompositions.some((allRenderedCompositions) => {
        return allRenderedCompositions === comp;
      })
    ) {
      return {
        id: comp,
        name:
          allCompositions.find((composition) => composition.id === comp)
            ?.name || "",
      };
    }
  }
  return null;
}

export async function continueRender() {
  const {
    compositionStore: { compositionCurrentlyRendering },
  } = rootStore;
  const comp = compositionCurrentlyRendering;

  try {
    rootStore.renderStore.updateSaveCompStepStatus(SaveCompStep.Rendering);
    rootStore.renderStore.updateSaveCompStatus(AsyncOpState.Saving);
    const beforeRenderState = await runBeforeRender(comp.id);
    rootStore.renderStore.setBeforeRenderState(beforeRenderState);

    rootStore.uiStore.setRenderProcessVisible();
    const tmpData = await createTmpDir();
    rootStore.renderStore.setFilesFolderPath(tmpData.dir);
    defaultSettings.id = comp.id;
    defaultSettings.destination = tmpData.file;
    defaultSettings.absoluteURI = tmpData.file;

    const eScript = jsxConstFunctions.renderComposition.replace(
      "%VALUE%",
      JSON.stringify(defaultSettings)
    );
    await runJsxScript(eScript);
  } catch (e) {
    console.error(e);
  }
}

async function renderComposition(comp: { id: number; name: string }) {
  const {
    uiStore: {
      setSizeProblemData,
      setSizeProblemDataModalVisible,
      setWarningModalVisible,
      setWarningData,
      setProblemLayersData,
    },
    compositionStore: {
      setCompositionCurrentlyRendering,
      setRenderedCompositions,
    },
    renderStore: { doNotAskFileSize, ignoreProblemLayers },
  } = rootStore;

  rootStore.uiStore.setRenderProcessVisible();
  rootStore.uiStore.setRenderProgress(0);

  setCompositionCurrentlyRendering(comp);

  const { fileSizes, problematicLayers, warningLayers } =
    await findProblemsBeforeRender();
  const totalFilesSize =
    fileSizes.reduce((acc: number, curr) => {
      if (fileFormat(curr) !== "video") {
        // check if the file extension is not in the list of video formats. Videos do not affect performance because of hls
        acc += curr.sizeInKb;
      }
      return acc;
    }, 0) / 1000;
  // errorTypes will contain an array of all active errors
  const errorTypes: string[] = [];
  for (const [key, value] of Object.entries(problematicLayers)) {
    value.length > 0 && errorTypes.push(key);
  }
  // Scene names can only contain characters, numbers, underscores, dashes and spaces
  const regex = /^[a-zA-Z0-9_\- ]+$/;
  if (!regex.test(comp.name)) {
    Object.assign(problematicLayers, { NameError: [""] });
    errorTypes.push("NameError");
  }
  // If an error is present in the composition
  if (errorTypes.length > 0 && !ignoreProblemLayers) {
    analyticsInstance.sendAnalytics("render", {
      val: "fail",
      m: errorTypes,
      sceneName: comp.name,
    });
    setProblemLayersData(problematicLayers, comp.name);
    setRenderedCompositions(comp.id, false);
    testBeforeRender(); // Continues to the next scene. Error is non blocking for other compositions
  } else if (totalFilesSize > maxFilesSizeLimit && !doNotAskFileSize) {
    setSizeProblemData({ totalFilesSize, fileSizes });
    setSizeProblemDataModalVisible();
  } else if (warningLayers?.length) {
    setWarningModalVisible();
    setWarningData(warningLayers);
  } else {
    continueRender();
  }
}

export async function testBeforeRender() {
  const {
    uiStore: { getProblemLayers, setProblemLayersModalVisible },
    renderStore: { setIsRendering },
  } = rootStore;
  const nextComposition = getNextComposition();
  // If there is a composition in the selectedComposition that has not been rendered - render, else - continue to next step
  if (nextComposition) {
    setIsRendering(true);
    analyticsInstance.sendAnalytics("render", {
      val: "start",
      sceneName: nextComposition.name,
    });
    await renderComposition(nextComposition);
  } else {
    if (getProblemLayers().length > 0) {
      setProblemLayersModalVisible();
    }
    finalRenderStep();
  }
}

export async function finalRenderStep() {
  rootStore.renderStore.setDoNotAskFileSize(false);
  rootStore.renderStore.setProcessIsCancelled(false);
  rootStore.projectStore.loadProjects();

  //Reset the renderedCompositions
  rootStore.renderStore.setIsRendering(false);
  rootStore.renderStore.updateSaveCompStatus(AsyncOpState.Success);
}
